import type { RouteLocationNormalized, RouteRecordRaw } from "vue-router";

export type PageOptions = RouteRecordRaw & {
    id: number;
    name: string;
    title: string;
    icon?: string;
    meta?: {
        requiresAuth?: boolean;
        topLevel?: boolean;
    };
};
export interface ConfigOptions
{
    backendUrl: string;
    title: string;
    author: string;
    pages: PageOptions[];
    version: string;
}

export class PageCollection extends Array<PageOptions>
{
    public get topLevel(): PageOptions[]
    {
        return this.filter((page) => page.meta?.topLevel);
    }

    public getByPath(path: string): PageOptions | undefined
    {
        return this.find((page) => page.path === path);
    }
    public getByRoute(route: RouteLocationNormalized): PageOptions | undefined
    {
        return this.getByPath(route.path);
    }
}
export class Config implements ConfigOptions
{
    protected _options: ConfigOptions;
    protected _pages: PageCollection;

    public get backendUrl(): string { return this._options.backendUrl; }
    public get title(): string { return this._options.title; }
    public get author(): string { return this._options.author; }
    public get version(): string { return this._options.version; }

    public get pages(): PageCollection { return this._pages; }

    public constructor(options: ConfigOptions)
    {
        this._options = options;
        this._pages = new PageCollection(...this._options.pages);
    }
}

export default new Config({
    backendUrl: import.meta.env.VITE_BACKEND_URL,

    title: "Dashboard",
    author: "Dinobros Srl",
    pages: [
        {
            id: 0x1,
            name: "home",
            path: "/",
            component: () => import("./pages/HomePage.vue"),
            title: "Home"
        },
        {
            id: 0x21,
            name: "user-log_in",
            path: "/login",
            component: () => import("./pages/user/LoginPage.vue"),
            title: "Login"
        },
        {
            id: 0x22,
            name: "user-log_out",
            path: "/logout",
            component: () => import("./pages/user/LogoutPage.vue"),
            title: "Logout"
        },
        {
            id: 0x31,
            name: "project-overview",
            path: "/project/:id/",
            component: () => import("./pages/OverviewPage.vue"),
            title: "Panoramica",
            icon: "bar-chart-2",
            meta: {
                requiresAuth: true,
                topLevel: true
            }
        },
        {
            id: 0x32,
            name: "project-users",
            path: "/project/:id/users/",
            component: () => import("./pages/UsersPage.vue"),
            title: "Utenti",
            icon: "users",
            meta: {
                requiresAuth: true,
                topLevel: true
            }
        },
        {
            id: 0x331,
            name: "project-events",
            path: "/project/:id/events/",
            component: () => import("./pages/EventsPage.vue"),
            title: "Eventi",
            icon: "calendar",
            meta: {
                requiresAuth: true,
                topLevel: true
            }
        },
        {
            id: 0x332,
            name: "project-realtime",
            path: "/project/:id/realtime/",
            component: () => import("./pages/RealtimePage.vue"),
            title: "Tempo reale",
            icon: "clock",
            meta: {
                requiresAuth: true,
                topLevel: true
            }
        },
        {
            id: 0x34,
            name: "project-leaderboards",
            path: "/project/:id/leaderboards/",
            component: () => import("./pages/LeaderboardsPage.vue"),
            title: "Classifiche",
            icon: "award",
            meta: {
                requiresAuth: true,
                topLevel: true
            }
        },
        {
            id: 0x35,
            name: "project-quizzes",
            path: "/project/:id/quizzes/",
            component: () => import("./pages/QuizzesPage.vue"),
            title: "Quiz",
            icon: "help-circle",
            meta: {
                requiresAuth: true,
                topLevel: true
            }
        },
        {
            id: 0x36,
            name: "project-colyseus",
            path: "/project/:id/colyseus/",
            component: () => import("./pages/ColyseusPage.vue"),
            title: "Colyseus",
            icon: "zap",
            meta: {
                requiresAuth: true,
                topLevel: true
            }
        }
    ],
    version: "1.4.0"
});
